/**
 * Filters Component
 *
 * Search and filter controls below the map.
 * Features:
 * - Search input with fuzzy matching
 * - Service tags multi-select
 * - Sector dropdown
 * - Province/location dropdown
 * - Clear all filters button
 */

import { useState } from 'react';
import { MagnifyingGlass, X, CaretDown } from '@phosphor-icons/react';
import { COLORS, PROVINCES, SECTORS, SERVICE_TAGS } from '../../lib/constants';

interface FiltersProps {
  onSearchChange?: (query: string) => void;
  onServiceTagsChange?: (tags: string[]) => void;
  onSectorChange?: (sector: string | null) => void;
  onProvinceChange?: (province: string | null) => void;
  onClearFilters?: () => void;
  resultCount?: number;
  totalCount?: number;
}

const styles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '10px',
    padding: '14px 18px',
    backgroundColor: COLORS.parchment,
    borderTop: `1px solid ${COLORS.graphite}20`,
    alignItems: 'center',
  },
  searchWrapper: {
    position: 'relative' as const,
    flex: '1 1 180px',
    maxWidth: '340px',
    minWidth: '140px',
  },
  searchInput: {
    width: '100%',
    padding: '10px 36px 10px 40px',
    fontSize: '15px',
    border: `2px solid ${COLORS.graphite}30`,
    borderRadius: 0,
    backgroundColor: 'white',
    color: COLORS.graphite,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  clearSearchButton: {
    position: 'absolute' as const,
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    padding: 0,
    border: 'none',
    borderRadius: 0,
    backgroundColor: `${COLORS.graphite}15`,
    color: COLORS.graphite,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  searchIcon: {
    position: 'absolute' as const,
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.graphite,
    opacity: 0.5,
    pointerEvents: 'none' as const,
  },
  select: {
    padding: '10px 34px 10px 12px',
    fontSize: '15px',
    border: `2px solid ${COLORS.graphite}30`,
    borderRadius: 0,
    backgroundColor: 'white',
    color: COLORS.graphite,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    backgroundImage: 'none',
    minWidth: '110px',
  },
  selectWrapper: {
    position: 'relative' as const,
    display: 'inline-flex',
    alignItems: 'center',
  },
  selectIcon: {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.graphite,
    opacity: 0.5,
    pointerEvents: 'none' as const,
  },
  clearButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 14px',
    fontSize: '15px',
    border: `2px solid ${COLORS.graphite}30`,
    borderRadius: 0,
    backgroundColor: 'white',
    color: COLORS.graphite,
    cursor: 'pointer',
    transition: 'background-color 0.2s, border-color 0.2s',
  },
};

export function Filters({
  onSearchChange,
  onServiceTagsChange,
  onSectorChange,
  onProvinceChange,
  onClearFilters,
  resultCount,
  totalCount,
}: FiltersProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServiceTags, setSelectedServiceTags] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>('');
  const [selectedProvince, setSelectedProvince] = useState<string>('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearchChange?.('');
  };

  const handleServiceTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value && !selectedServiceTags.includes(value)) {
      const newTags = [...selectedServiceTags, value];
      setSelectedServiceTags(newTags);
      onServiceTagsChange?.(newTags);
    }
    // Reset the select after selection
    e.target.value = '';
  };

  const handleSectorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedSector(value);
    onSectorChange?.(value || null);
  };

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedProvince(value);
    onProvinceChange?.(value || null);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedServiceTags([]);
    setSelectedSector('');
    setSelectedProvince('');
    onClearFilters?.();
  };

  const hasActiveFilters =
    searchQuery ||
    selectedServiceTags.length > 0 ||
    selectedSector ||
    selectedProvince;

  return (
    <div style={styles.container} data-testid="filters-container">
      {/* Search Input */}
      <div style={styles.searchWrapper}>
        <MagnifyingGlass size={20} style={styles.searchIcon} weight="bold" />
        <input
          type="text"
          placeholder="Zoeken..."
          value={searchQuery}
          onChange={handleSearchChange}
          style={styles.searchInput}
          data-testid="search-input"
        />
        {searchQuery && (
          <button
            onClick={handleClearSearch}
            style={styles.clearSearchButton}
            data-testid="clear-search-button"
            title="Zoekopdracht wissen"
            aria-label="Zoekopdracht wissen"
          >
            <X size={16} weight="bold" />
          </button>
        )}
      </div>

      {/* Service Tags Dropdown */}
      <div style={styles.selectWrapper}>
        <select
          onChange={handleServiceTagChange}
          style={styles.select}
          defaultValue=""
          data-testid="service-tags-filter"
        >
          <option value="" disabled>
            Diensten
          </option>
          {SERVICE_TAGS.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
        <CaretDown size={16} style={styles.selectIcon} weight="bold" />
      </div>

      {/* Sector Dropdown */}
      <div style={styles.selectWrapper}>
        <select
          value={selectedSector}
          onChange={handleSectorChange}
          style={styles.select}
          data-testid="sector-filter"
        >
          <option value="">Sector</option>
          {SECTORS.map((sector) => (
            <option key={sector.value} value={sector.value}>
              {sector.label}
            </option>
          ))}
        </select>
        <CaretDown size={16} style={styles.selectIcon} weight="bold" />
      </div>

      {/* Province/Location Dropdown */}
      <div style={styles.selectWrapper}>
        <select
          value={selectedProvince}
          onChange={handleProvinceChange}
          style={styles.select}
          data-testid="province-filter"
        >
          <option value="">Provincie</option>
          {PROVINCES.map((province) => (
            <option key={province} value={province}>
              {province}
            </option>
          ))}
        </select>
        <CaretDown size={16} style={styles.selectIcon} weight="bold" />
      </div>

      {/* Clear Filters Button */}
      <button
        onClick={handleClearFilters}
        style={{
          ...styles.clearButton,
          opacity: hasActiveFilters ? 1 : 0.5,
          cursor: hasActiveFilters ? 'pointer' : 'default',
        }}
        disabled={!hasActiveFilters}
        data-testid="clear-filters-button"
      >
        <X size={16} weight="bold" />
        Wissen
      </button>

      {/* Result Count */}
      {resultCount !== undefined && totalCount !== undefined && (
        <span
          style={{
            fontSize: '15px',
            color: `${COLORS.graphite}99`,
            marginLeft: 'auto',
          }}
          data-testid="result-count"
        >
          {resultCount === totalCount
            ? `${totalCount} organisaties`
            : `${resultCount} van ${totalCount} organisaties`}
        </span>
      )}

      {/* Selected Service Tags Display */}
      {selectedServiceTags.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' as const, width: '100%' }}>
          {selectedServiceTags.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                fontSize: '14px',
                backgroundColor: `${COLORS.royalBlue}15`,
                color: COLORS.royalBlue,
                borderRadius: 0,
                border: `1px solid ${COLORS.royalBlue}30`,
              }}
            >
              {tag}
              <X
                size={14}
                weight="bold"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const newTags = selectedServiceTags.filter((t) => t !== tag);
                  setSelectedServiceTags(newTags);
                  onServiceTagsChange?.(newTags);
                }}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
